
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import CategorySectionFull from '../../../components/CategorySections/CategorySectionFull';
import Layout from '../../../components/Layout';
import { StaticPathParams } from '../../../typings/staticPaths';
import sanitize from 'sanitize-html-react';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import { basicSEO } from 'helpers/jsx';
import { withForcePlanToLoggedInUnsubs } from 'context';
import Celebrity from 'models/Celebrity';
import { Celebrity as CelebrityAPI } from 'typings/api';
import DramoxImage from 'components/DramoxImage';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { createRedirectIfNeeded, kinoboxLink, Permalink } from 'helpers';
import { locales } from 'i18n';
import { baseUrl } from 'config/routeTable';
import { useMemo } from 'react';
import { useLang } from 'context/langContext';
import slugify from 'slugify';

interface Props {
  celebrityJSON: CelebrityAPI;
  countryCode: string;
}

const CelebrityDetail: NextPage<Props> = ({ celebrityJSON, countryCode }) => {
  const { t } = useTranslation();
  const { locale } = useLang();

  const celebrity = Celebrity.fromJSON(celebrityJSON);

  const structuredSEOData = useMemo(() => {
    return {
      '@context': 'https://schema.org/',
      '@type': 'Person',
      name: celebrity.name,
      image: celebrity.poster_url,
    };
  }, [celebrity]);

  const [seoDescription, truncateSeoDescription] = useMemo(() => {
    if (celebrity.about) {
      return [celebrity.about, true];
    }

    const plays = celebrity.contributions
      ?.map((c) => c.play?.title)
      .filter((play) => Boolean(play))
      .slice(0, 2);

    if (plays.length > 0) {
      return [
        t('seo:celebrity_default_about', {
          name: celebrity.name,
          plays: plays.join(', '),
          theatre: celebrity.contributions[0]?.play?.theatre.name,
        }),
        false,
      ];
    } else {
      return [t('seo:playlist_currently_watching_description'), false];
    }
  }, [celebrity, t]);

  const celebrityAbout = useMemo(() => {
    if (!celebrity.about) {
      return '';
    }

    let ret = sanitize(celebrity.about);
    if (celebrity.kinobox && Boolean(ret)) {
      const name = celebrity?.names.find((n) => n.language === locale)?.value;
      if (name) {
        const link = `<a href="${kinoboxLink(
          'person',
          celebrity.kinobox,
          name,
        )}" target="_blank" rel="external">${name}</a>`;
        ret = ret.replace(new RegExp(name, 'g'), link);
      }
    }

    return ret;
  }, [celebrity.about, locale]);

  return (
    <Layout countryCode={countryCode}>
      <Head>
        {basicSEO(
          t('seo:celebrity_title', { name: celebrity.name }),
          seoDescription,
          celebrity.poster_url,
          null,
          truncateSeoDescription,
        )}
        ;
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredSEOData),
          }}
        ></script>
        {locales.map((langLocale) => (
          <link
            key={langLocale}
            rel="alternate"
            href={`${baseUrl(langLocale)}${Permalink.celebrity(celebrity, langLocale)}`}
            hrefLang={langLocale}
          />
        ))}
        <link
          key="lang-default"
          rel="alternate"
          href={`${baseUrl('en')}${Permalink.celebrity(celebrity, 'en')}`}
          hrefLang="x-default"
        />
      </Head>
      <main className="author-detail">
        <div className="info-wrapper">
          <div className="image-placeholder">
            {celebrity?.poster_url ? (
              <DramoxImage src={celebrity?.poster_url} alt={celebrity.name} width={170} height={170} quality={90} />
            ) : (
              celebrity.shortName
            )}
          </div>
          <div className="text-wrapper">
            <h1>{celebrity?.name}</h1>
            {Boolean(celebrityAbout) && <div className="about" dangerouslySetInnerHTML={{ __html: celebrityAbout }} />}
          </div>
        </div>
        {celebrity.flattenedContributions?.map((role, i) => (
          <CategorySectionFull key={i} headline={role} index={i} plays={celebrity.playsForRole(role)} />
        ))}
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  let { slug } = ctx.params ?? {};

  const { locale, apiClient, countryCode } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  try {
    slug = slug.replace('NaN-', '');
    const [id, ..._slug] = slug.split('-');
    let res: CelebrityAPI;

    // old urls without ids
    if (isNaN(+id)) {
      res = await apiClient.getCelebrityDetail(slug);
    } else {
      res = await apiClient.getCelebrityDetail(+id);
    }
    console.log(res);
    const celebrity = Celebrity.fromJSON(res);

    const localisedSlug = celebrity.localisedSlug(locale);

    if (_slug.join('-') !== localisedSlug) {
      return {
        redirect: {
          permanent: true,
          destination: Permalink.celebrity(celebrity, locale),
        },
      };
    }

    return {
      props: { celebrityJSON: res, countryCode },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

export default withForcePlanToLoggedInUnsubs(CelebrityDetail);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/celebrity/[slug]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  